/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component } from "../component";
import { EventEmitter } from "../event-emitter";

export interface DynamicLayoutProps<
  DynamicLayoutEvent,
  DynamicLayoutEventPayload = undefined,
> {
  fireEvent?: (
    event: DynamicLayoutEvent,
    payload?: DynamicLayoutEventPayload
  ) => void;
}

/**
 * A React component that serves as a layout. It is handed over an array of components
 * and takes care of rendering them. It can also have a layout state and events.
 */
export type PimoDynamicReactLayout<
  LayoutState = undefined,
  LayoutEventNames = undefined,
  LayoutEventPayload = undefined,
  ComponentLayoutProps = object,
  // if `LayoutState` is `undefined`, and we'd use it directly, it'd make the component's props (`React.FC<props>`) into `any`, which we want to avoid for type safety. Hence, this helper.
  LayouStateThatIsNeverUndefined = LayoutState extends undefined
    ? object
    : LayoutState,
> = React.FC<
  {
    components: Component<any, any, any, any, ComponentLayoutProps>[];
  } & LayouStateThatIsNeverUndefined &
    DynamicLayoutProps<LayoutEventNames, LayoutEventPayload>
>;

export abstract class DynamicLayout<
  LayoutState = any,
  LayoutEventNames = any,
  LayoutEventPayload = any,
  ComponentLayoutProps = any,
> extends EventEmitter<LayoutEventNames, LayoutEventPayload> {
  // the following fields will never be used in actual code, but are required
  // for proper type inference of the generic types
  eventNames?: LayoutEventNames;
  eventPayload?: LayoutEventPayload;
  layoutState?: LayoutState;
  componentLayoutProps?: ComponentLayoutProps;

  abstract getLayoutComponent(): PimoDynamicReactLayout<
    LayoutState,
    LayoutEventNames,
    LayoutEventPayload,
    ComponentLayoutProps
  >;
}
